import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "wouter";
import { syncBodyScrollLockOffset } from "@/lib/dom/bodyScrollLock";
import { landingSectionTargets } from "@/lib/navigationConfig";

/**
 * ScrollRestoration ensures that client-side navigations start at the top
 * of the document. Without this the scroll position from the previous route
 * is preserved, which is undesirable when navigating from long pages like
 * the marketing footer into legal content.
 */
export const ScrollRestoration = () => {
  const [location] = useLocation();
  const isBrowser = typeof window !== "undefined";

  const resetScrollPosition = () => {
    if (!isBrowser) return;

    // The homepage's section navigation owns scrolling for these known hashes. Check on
    // every retry as a visitor can select a section while the initial 300ms guard is active.
    if (window.location.pathname === "/" && Object.hasOwn(landingSectionTargets, window.location.hash)) return;

    // Ensure all common scroll containers are reset.
    window.scrollTo({ top: 0, behavior: "auto" });
    if (typeof document !== "undefined") {
      const { documentElement, body } = document;
      if (documentElement) {
        documentElement.scrollTop = 0;
      }
      if (body) {
        body.scrollTop = 0;
      }
    }

    syncBodyScrollLockOffset(0);
  };

  const useBrowserLayoutEffect = isBrowser ? useLayoutEffect : useEffect;

  useBrowserLayoutEffect(() => {
    if (!isBrowser) {
      return;
    }

    resetScrollPosition();

    const raf = typeof window !== "undefined" ? window.requestAnimationFrame : undefined;
    const cancelRaf = typeof window !== "undefined" ? window.cancelAnimationFrame : undefined;
    let rafId: number | null = null;
    let attempts = 0;
    const maxAttempts = 5;

    const enforceTop = () => {
      attempts += 1;
      resetScrollPosition();
      if (attempts < maxAttempts && typeof raf === "function") {
        rafId = raf(enforceTop);
      }
    };

    if (typeof raf === "function") {
      rafId = raf(enforceTop);
    }

    const timeoutId = window.setTimeout(() => resetScrollPosition(), 0);

    let guardActive = true;
    const scrollGuard = () => {
      if (!guardActive) return;
      if (window.scrollY > 0 || document.documentElement.scrollTop > 0 || document.body.scrollTop > 0) {
        resetScrollPosition();
      }
    };

    window.addEventListener("scroll", scrollGuard, { passive: true });

    const guardTimeoutId = window.setTimeout(() => {
      guardActive = false;
      window.removeEventListener("scroll", scrollGuard);
    }, 300);

    return () => {
      if (rafId !== null && typeof cancelRaf === "function") {
        cancelRaf(rafId);
      }
      window.clearTimeout(timeoutId);
      guardActive = false;
      window.removeEventListener("scroll", scrollGuard);
      window.clearTimeout(guardTimeoutId);
    };
  }, [isBrowser, location]);

  return null;
};
